import { useCallback, useEffect, useRef } from 'react';

import useAudioPlayer from '@/shared/hooks/useAudioPlayer';

import type { TracingGameData } from '../types';

type TracingGameVoiceLine = 'intro' | 'complete' | 'fail';

const getVoiceLineUrl = (
	voiceAudio: TracingGameData['presentation']['voice_audio'],
	voiceLine: TracingGameVoiceLine,
) => {
	switch (voiceLine) {
		case 'intro':
			return voiceAudio?.intro_audio_url;
		case 'complete':
			return voiceAudio?.complete_audio_url;
		case 'fail':
			return voiceAudio?.fail_audio_url;
	}
};

export const useTracingGameVoiceAudio = (
	voiceAudio: TracingGameData['presentation']['voice_audio'],
	audioOn: boolean,
) => {
	const completionRef = useRef<(() => void) | null>(null);
	const completePending = useCallback(() => {
		const onComplete = completionRef.current;
		completionRef.current = null;
		onComplete?.();
	}, []);
	const { play: playIntro, stop: stopIntro } = useAudioPlayer({
		src: voiceAudio?.intro_audio_url,
		onEnd: completePending,
	});
	const { play: playComplete, stop: stopComplete } = useAudioPlayer({
		src: voiceAudio?.complete_audio_url,
		onEnd: completePending,
	});
	const { play: playFail, stop: stopFail } = useAudioPlayer({
		src: voiceAudio?.fail_audio_url,
		onEnd: completePending,
	});

	const stop = useCallback(() => {
		completionRef.current = null;
		stopIntro();
		stopComplete();
		stopFail();
	}, [stopComplete, stopFail, stopIntro]);

	const play = useCallback(
		(voiceLine: TracingGameVoiceLine, onComplete?: () => void) => {
			stop();
			const url = getVoiceLineUrl(voiceAudio, voiceLine);
			if (!audioOn || !url) {
				onComplete?.();
				return;
			}
			completionRef.current = onComplete ?? null;

			switch (voiceLine) {
				case 'intro':
					playIntro();
					break;
				case 'complete':
					playComplete();
					break;
				case 'fail':
					playFail();
					break;
			}
		},
		[audioOn, playComplete, playFail, playIntro, stop, voiceAudio],
	);

	useEffect(() => {
		if (!audioOn) {
			completePending();
		}
	}, [audioOn, completePending]);

	return { play, stop };
};
